import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { OpenCart } from '../../services/open-carts-list-config/open-carts-list-config.service';
import { ListItemRendererComponent } from 'gung-list';
import { DateUtilService, GungAccordionComponent, gungComparatorHelper } from 'gung-common';
import { first, forkJoin, map, mergeMap, Observable, of } from 'rxjs';
import { Product } from '../../models';
import { ProductService } from '../../services/products/product.service';
import { PriceService } from '../../services/price/price.service';
import { GungModalService } from '../../services/gung-modal/gung-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { SelectedCustomerService } from '../../services/selected-customer/selected-customer.service';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';
import { CartService } from '../../services/cart/cart.service';

@Component({
  selector: 'lib-open-cart-table',
  standalone: false,
  templateUrl: './open-cart-table.component.html',
  styleUrl: './open-cart-table.component.scss'
})
export class OpenCartTableComponent extends ListItemRendererComponent<OpenCart[]> {
  protected dateUtilService = inject(DateUtilService);
  protected productService = inject(ProductService);
  protected priceService = inject(PriceService);
  protected modalService = inject(GungModalService);
  protected translateService = inject(TranslateService);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected gungFlowService = inject(GungFlowService);
  protected cartService = inject(CartService);

  mapData: {
    [id: string]: {
      qty: number;
      product: Product;
      productId: string;
    }[];
  } = {};
  saveData = {};
  public products: { [id: string]: Product }
  @ViewChild('acc') accordion: GungAccordionComponent;

  totalPrice = 0; // Should not be used, use totalPriceByCart
  totalPriceByCart: { [cartId: string]: number } = {};
  currencyCode: string;

  protected fetchData(cart: OpenCart): Observable<{ qty: number; product: Product; productId: string }[]> {
    if (!this.saveData[cart.id]) {
      this.saveData[cart.id] = false;
    }
    this.totalPrice = 0; // Should not be used, use totalPriceByCart
    this.totalPriceByCart[cart.id] = 0;
    const ids = cart.rows.map(row => row.productId);
    return this.productService.getFullProductsByIds(ids).pipe(
      first(),
      mergeMap(products => {
        this.products = products.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
        const productsIds = products.map(p => p.id);
        return forkJoin({
          products: of(products),
          prices: this.priceService.getCustomerPrices(productsIds, cart.customerId).pipe(first())
        });
      }),
      map(({ products, prices }) => {
        const data: { qty: number; product: Product; productId: string }[] = [];

        cart.rows = cart.rows.sort((c1, c2) => gungComparatorHelper(c1?.extra.cartOrder, c2?.extra.cartOrder, 1));
        for (const [i, row] of cart.rows.entries()) {
          if (!row.extra.cartOrder) {
            row.extra.cartOrder = i + 1;
          }
        }

        cart.rows.map(row => {
          const qty = row.qty;
          // in case product was not presented in the response
          // it means the product is excluded (due to blocked, not belong to flow, removed)
          const product: Product = products.find(p => p.id === (row.productId));

          if (product) {
            const price = prices.find(pr => pr.productId === product.id);
            if (price) {
              product.extra.price = price.customerNetPrice;
              if (this.currencyCode == null) {
                this.currencyCode = price.customerNetPrice.currencyCode;
              }
              this.totalPrice += price.customerNetPrice.value * qty; // Should not be used, use totalPriceByCart
              this.totalPriceByCart[cart.id] += price.customerNetPrice.value * qty;
            }
            data.push(this.mapDataProduct(qty, product, row));
          } else {
            // To add the productId to list of excluded ids when loading cart
            data.push(this.mapDataProduct(qty, undefined, row));
          }
        });

        return data;
      })
    );
  }

  protected mapDataProduct(qty, product, row) {
    return {
      qty,
      product,
      productId: row.productId || row.id
    };
  }

  fetchProductData(cart: OpenCart, forceUpdate?: boolean): void {
    this.fetchData(cart).subscribe(data => {
      this.setMappedData(cart.id, data, forceUpdate);
    });
  }

  protected setMappedData(cartId: string, data: { qty: number; product: Product; productId: string }[], forceUpdate?: boolean): void {
    // include only all the row whose product is not excluded
    this.mapData[cartId] = [...data
      .filter(row => !!row.product)
      .map(row => {
        return {
          qty: row.qty,
          product: row.product,
          productId: row.productId
        };
      })]
    
  }

  deleteCart(cart: OpenCart, index: number) {
    this.modalService.openConfirmYesNoModal(
      null,
      this.translateService.instant('CONFIRM_DELETION', { value: cart.id }) + '?',
      null,
      this.translateService.instant('YES'),
      this.translateService.instant('NO')
    ).then((res)=>{
      if(res){
        if(cart.currentCart){
          this.cartService.clearCart();
        }else{
          localStorage.removeItem(cart.id);
        }
        this.data.splice(index,1);
      }
    })

  }

  showOpenCart(cart: OpenCart) {
    if (!this.accordion.isExpanded(cart.id)) {
      this.accordion.expand(cart.id);
    } else {
      this.accordion.collapse(cart.id);
    }
  }

  loadCart(cart){
    const match = cart.id.match(/^cart_(.+)_(\d+)$/);

    if (match) {
      const customerId = match[1];
      const flowId = match[2];
      this.selectedCustomerService.selectCustomer(customerId);
      this.gungFlowService.selectFlow(flowId);
    }
  }
}
